import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import auth from 'utils/auth';
import { JobSeekingStatus } from 'types/types';
import { SESSION_STORAGE_KEYS } from 'utils/constants';
import { MatchQuizStatusStrings } from 'features/Connect/utils/connectEnums';
import { DAYS_TO_EXPIRE_FRESHNESS, DAYS_TO_WARN_FRESHNESS } from '../../utils/connectConstants';
import { useRouteMatch } from 'react-router-dom';
import { connectRedirectUrls } from 'utils/constants/connect';

export function useShouldDisplayRefreshActiveJobSeekingStatusBanner(): boolean {
  const { jobSeekingStatus, jobSeekingStatusUpdatedAt } = useConnectContext();
  const isConnectPreferencesPage = useRouteMatch(connectRedirectUrls.PREFERENCES);
  const daysLeftOfActiveJobSeekingStatus = getDaysLeftBeforeActiveJobSeekingStatusExpires(
    jobSeekingStatus,
    jobSeekingStatusUpdatedAt
  );

  const daysLeftToWarn = DAYS_TO_EXPIRE_FRESHNESS - DAYS_TO_WARN_FRESHNESS;

  if (
    isConnectPreferencesPage ||
    jobSeekingStatus !== JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES ||
    daysLeftOfActiveJobSeekingStatus === null
  ) {
    return false;
  } else if (
    daysLeftOfActiveJobSeekingStatus <= daysLeftToWarn &&
    daysLeftOfActiveJobSeekingStatus > 0
  ) {
    return true;
  } else {
    return false;
  }
}

export function useShouldDisplayMatchesNotificationBanner(): boolean {
  const { candidateMatches } = useConnectContext();
  const isAuthenticated = auth.loggedIn();
  return isAuthenticated && candidateMatches?.pending?.length > 0;
}

export function useShouldDisplayMatchQuizBanner(): boolean {
  const isAuthenticated = auth.loggedIn();
  const user = auth.getUser();
  const isCandidateUser = user?.profile ? auth.isCandidateUser(user) : false;
  const { matchQuizStatus, jobSeekingStatus } = useConnectContext();

  return (
    isAuthenticated &&
    isCandidateUser &&
    jobSeekingStatus === JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES &&
    matchQuizStatus !== MatchQuizStatusStrings.COMPLETE
  );
}

export const getDaysLeftBeforeActiveJobSeekingStatusExpires = (
  jobSeekingStatus: string,
  jobSeekingStatusUpdatedAt: string
): number | null => {
  if (
    !jobSeekingStatus ||
    !jobSeekingStatusUpdatedAt ||
    jobSeekingStatus !== JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES
  ) {
    return null;
  }

  const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;

  const updatedAt = new Date(jobSeekingStatusUpdatedAt);
  const expirationDate = new Date(
    updatedAt.getTime() + DAYS_TO_EXPIRE_FRESHNESS * MILLISECONDS_IN_A_DAY
  );

  // Truncate both to midnight UTC to calculate the difference infull calendar days
  const today = new Date();
  const utcToday = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const utcExpiration = new Date(
    Date.UTC(
      expirationDate.getUTCFullYear(),
      expirationDate.getUTCMonth(),
      expirationDate.getUTCDate()
    )
  );

  // Calculate days between today and expiration date
  const diffTime = utcExpiration.getTime() - utcToday.getTime();

  // If status expires today, round up the partial day to 1 day
  if (diffTime === 0) {
    return 1;
  }

  // If status has already expired, return null
  if (diffTime < 0) {
    return null;
  }

  // If status expires after today, return the number of days left
  const daysLeft = Math.floor(diffTime / MILLISECONDS_IN_A_DAY);
  return daysLeft;
};

export function resetDismissedBanners() {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.DISMISSED_BANNERS);
}
